import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpBackend, HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface ApiHit {
  count: number;
  componentName: string;
  pageName: string;
  apiUrl: string;
  apiMethod: string;
  firstHitTimestamp: number;
  lastHitTimestamp: number;
}

@Injectable()
export class ApiLoggingInterceptor implements HttpInterceptor {
  private http: HttpClient;
  private apiHitsAggregator: Map<string, ApiHit> = new Map();
  private flushTimeout: any = null;
  private readonly flushDelayMs = 5000; // Send aggregated data every 5 seconds
  private readonly minHitsBeforeFlush = 3; // Or flush if we have at least 3 unique APIs
  private readonly maxAggregationWindowMs = 30000; // Max 30 seconds aggregation

  constructor(private httpBackend: HttpBackend) {
    this.http = new HttpClient(this.httpBackend);
    
    // Flush on page unload to ensure we don't lose data
    window.addEventListener('beforeunload', () => {
      this.flushAggregatedData();
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    try {
      // Skip logging for logging endpoint itself
      const isUserHitApi = req.url?.includes('UserHitApi') || req.url?.includes('/UserHitApi.php');
      
      // Skip OPTIONS, preflight, and static assets
      if (req.method === 'OPTIONS' || isUserHitApi) {
        return next.handle(req);
      }
      
      if (/\.(js|css|map|svg|png|jpg|jpeg|gif|ico|woff|woff2|ttf)$/.test(req.url || '')) {
        return next.handle(req);
      }
      
      // Skip Angular's internal ngcc or other framework calls
      if (req.url?.includes('ngcc') || req.url?.includes('ngsw')) {
        return next.handle(req);
      }

      const componentName = this.getComponentNameFromUrl(req.url);
      const pageName = this.getPageNameFromUrl(req.url);
      const key = `${req.method}|${componentName}|${req.url}`;
      const now = Date.now();
      
      // Aggregate this API hit
      if (this.apiHitsAggregator.has(key)) {
        const existing = this.apiHitsAggregator.get(key)!;
        existing.count++;
        existing.lastHitTimestamp = now;
      } else {
        this.apiHitsAggregator.set(key, {
          count: 1,
          componentName,
          pageName,
          apiUrl: req.url,
          apiMethod: req.method,
          firstHitTimestamp: now,
          lastHitTimestamp: now
        });
      }
      
      // Schedule flush if not already scheduled
      this.scheduleFlush();
      
    } catch (e) {
      console.error('ApiLoggingInterceptor error', e);
    }

    return next.handle(req);
  }

  private scheduleFlush(): void {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
    }
    
    this.flushTimeout = setTimeout(() => {
      this.flushAggregatedData();
    }, this.flushDelayMs);
  }

  private flushAggregatedData(): void {
    if (this.apiHitsAggregator.size === 0) {
      return;
    }
    
    const now = Date.now();
    let shouldFlush = false;
    
    // Check conditions for flushing
    if (this.apiHitsAggregator.size >= this.minHitsBeforeFlush) {
      shouldFlush = true;
    } else {
      // Check if oldest hit exceeds max aggregation window
      for (const hit of this.apiHitsAggregator.values()) {
        if (now - hit.firstHitTimestamp >= this.maxAggregationWindowMs) {
          shouldFlush = true;
          break;
        }
      }
    }
    
    if (!shouldFlush && this.apiHitsAggregator.size > 0) {
      // Reschedule if we have data but not enough to flush yet
      this.scheduleFlush();
      return;
    }
    
    // Prepare aggregated payload
    const aggregatedHits = Array.from(this.apiHitsAggregator.values()).map(hit => ({
      ...hit,
      totalTimeWindowMs: hit.lastHitTimestamp - hit.firstHitTimestamp
    }));
    
    // Calculate summary
    const summary = {
      totalUniqueApis: aggregatedHits.length,
      totalApiCalls: aggregatedHits.reduce((sum, hit) => sum + hit.count, 0),
      departmentName: localStorage.getItem('deptName'),
      businessAreaId: localStorage.getItem('businessAreaId'),
      userName: localStorage.getItem('userName'),
      pageName: aggregatedHits[0]?.pageName || 'Unknown Page',
      timestamp: new Date().toISOString(),
      apiHits: aggregatedHits
    };
    
    // Send aggregated data (fire and forget)
    if (aggregatedHits.length > 0) {
      this.http.post(`${environment.apiUrl}/UserHitApi.php`, summary).subscribe({
        next: () => {},
        error: (err) => console.error('Failed to send aggregated API logs', err)
      });
    }
    
    // Clear aggregator
    this.apiHitsAggregator.clear();
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }
  }

  private getComponentNameFromUrl(url: string): string {
    try {
      const path = new URL(url, window.location.origin).pathname;
      const segments = path.split('/').filter(Boolean);
      
      // Try to detect Angular route/component patterns
      if (segments.length > 0) {
        // For API calls like /api/users, component might be 'users'
        if (segments[0] === 'api' && segments.length > 1) {
          return segments[1]; // /api/users -> users
        }
        return segments[0];
      }
      return 'Unknown';
    } catch {
      const match = url?.toString().match(/\/([^\/]+)\/?$/);
      return match ? match[1] : 'Unknown';
    }
  }

  private getPageNameFromUrl(_url: string): string {
    try {
      // Get current route from Angular router if available
      const currentUrl = window.location.pathname;
      const routeSegments = currentUrl.split('/').filter(Boolean);
      
      if (routeSegments.length > 0) {
        return routeSegments.join('/');
      }
      return document?.title?.replace(/[^a-zA-Z0-9\s]/g, '')?.trim() || 'Home';
    } catch {
      return 'Unknown Page';
    }
  }
}